import { Select, SelectItem } from '@nextui-org/react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { UXGenieTemplate } from './useUXGenieTemplateSelection';

export interface UXGenieTemplateSelectorProps {
  // Worker selection
  selectedUXGenieWorkerId: string;
  onWorkerChange: (workerId: string) => void;
  uxgenieWorkers: any[];

  // Template selection
  selectedUXGenieTemplateId: string;
  onTemplateChange: (templateId: string) => void;
  uxgenieTemplates: UXGenieTemplate[];

  // Layout variant: 'stacked' for ActionConfiguration, 'inline' for AgentCreation
  layout?: 'stacked' | 'inline';
}

// Helper to create selection handler
const createSelectionHandler = (onChange: (value: string) => void) => (keys: any) => {
  const selected = [...keys][0];
  onChange(selected ? String(selected) : '');
};

// Helper to get template display name
const getTemplateDisplayName = (template: UXGenieTemplate) => {
  return template.title && template.title.trim() !== ''
    ? template.title
    : template.aiworker_name || template.aiworker_usage_id;
};

// Shared worker select component
interface WorkerSelectProps {
  selectedWorkerId: string;
  onSelectionChange: (keys: any) => void;
  workers: any[];
  t: (key: string) => string;
}

function WorkerSelect({ selectedWorkerId, onSelectionChange, workers, t }: WorkerSelectProps) {
  return (
    <Select
      id="uxgenie_worker"
      placeholder={t('Select worker first')}
      size="sm"
      aria-label="uxgenie_worker"
      selectedKeys={selectedWorkerId ? new Set([selectedWorkerId]) : new Set()}
      onSelectionChange={onSelectionChange}
      startContent={<Icon icon="mdi:robot" className="text-primary" />}
      description={workers.length === 0 ? t('No UXGenie Templates available. Create workers in UXGenie.') : ''}
    >
      {workers.length === 0 ? (
        <SelectItem key="no-workers" value="" isDisabled>
          {t('No workers available')}
        </SelectItem>
      ) : (
        workers.map((worker: any) => (
          <SelectItem
            key={worker.aiworker_id}
            value={worker.aiworker_id}
            description={worker.description || 'UXGenie Template'}
          >
            {worker.name || worker.aiworker_id}
          </SelectItem>
        ))
      )}
    </Select>
  );
}

// Shared template select component
interface TemplateSelectProps {
  selectedTemplateId: string;
  onSelectionChange: (keys: any) => void;
  templates: UXGenieTemplate[];
  t: (key: string) => string;
}

function TemplateSelect({ selectedTemplateId, onSelectionChange, templates, t }: TemplateSelectProps) {
  return (
    <Select
      id="uxgenie_template"
      placeholder={t('Select template (optional)')}
      size="sm"
      aria-label="uxgenie_template"
      selectedKeys={selectedTemplateId ? new Set([selectedTemplateId]) : new Set()}
      onSelectionChange={onSelectionChange}
      startContent={<Icon icon="material-symbols:article" className="text-primary" />}
      description={templates.length === 0 ? t('No templates available for this worker.') : ''}
    >
      {templates.length === 0 ? (
        <SelectItem key="no-templates" value="" isDisabled>
          {t('No templates available')}
        </SelectItem>
      ) : (
        templates.map((template) => (
          <SelectItem
            key={template.aiworker_usage_id}
            value={template.aiworker_usage_id}
            description={`${template.status || 'succeeded'} • ${template.date || ''}`}
          >
            {getTemplateDisplayName(template)}
          </SelectItem>
        ))
      )}
    </Select>
  );
}

// Label components for different layouts
interface LabelProps {
  icon: string;
  title: string;
  description: string;
  htmlFor: string;
  layout: 'stacked' | 'inline';
}

function FieldLabel({ icon, title, description, htmlFor, layout }: LabelProps) {
  if (layout === 'inline') {
    return (
      <label htmlFor={htmlFor} className="flex flex-col w-[30%]">
        <span className="text-[#414651] font-[Poppins] font-medium text-sm leading-[20px] tracking-normal flex items-center gap-2">
          <Icon icon={icon} className="text-primary" width={18} />
          {title}
        </span>
        <div className="font-[Inter] font-normal text-sm leading-5 tracking-normal text-[#535862]">
          {description}
        </div>
      </label>
    );
  }

  return (
    <label htmlFor={htmlFor}>
      <span className="text-[#414651] font-[Poppins] font-medium text-sm leading-[20px] tracking-normal flex items-center gap-2">
        <Icon icon={icon} className="text-primary" width={18} />
        {title}
      </span>
      <span className="text-xs text-default-500 block mt-1">
        {description}
      </span>
    </label>
  );
}

/**
 * Shared component for UXGenie worker and template selection.
 * Used in both ActionConfiguration and AgentCreation forms.
 */
export function UXGenieTemplateSelector({
  selectedUXGenieWorkerId,
  onWorkerChange,
  uxgenieWorkers,
  selectedUXGenieTemplateId,
  onTemplateChange,
  uxgenieTemplates,
  layout = 'stacked'
}: UXGenieTemplateSelectorProps) {
  const { t } = useTranslation();

  const handleWorkerSelection = createSelectionHandler(onWorkerChange);
  const handleTemplateSelection = createSelectionHandler(onTemplateChange);

  const rowClass = layout === 'inline' ? 'flex flex-row gap-3' : 'flex flex-col gap-1';
  const selectWrapperClass = layout === 'inline' ? 'flex-1' : '';

  return (
    <>
      <div className={rowClass}>
        <FieldLabel
          icon="mdi:magic-staff"
          title={t('UXGenie Template')}
          description={t('Select a UXGenie Template to view templates')}
          htmlFor="uxgenie_worker"
          layout={layout}
        />
        <div className={selectWrapperClass}>
          <WorkerSelect
            selectedWorkerId={selectedUXGenieWorkerId}
            onSelectionChange={handleWorkerSelection}
            workers={uxgenieWorkers}
            t={t}
          />
        </div>
      </div>

      {selectedUXGenieWorkerId && (
        <div className={rowClass}>
          <FieldLabel
            icon="material-symbols:template-view"
            title={t('UXGenie Template')}
            description={t('Optional: Select a template for custom display')}
            htmlFor="uxgenie_template"
            layout={layout}
          />
          <div className={selectWrapperClass}>
            <TemplateSelect
              selectedTemplateId={selectedUXGenieTemplateId}
              onSelectionChange={handleTemplateSelection}
              templates={uxgenieTemplates}
              t={t}
            />
          </div>
        </div>
      )}
    </>
  );
}

export default UXGenieTemplateSelector;
